import { useEffect, useRef, useState } from "react";
import { Paperclip, FileText, Plus, X, Link2, Search } from "lucide-react";
import expenseAttachmentApi from "../api/expenseAttachmentApi";
import Modal from "./Modal";
import Btn from "./Btn";
import Alert from "./Alert";

// Where the backend serves uploaded files from. The API base is ".../api";
// file URLs the backend hands out are server-relative
// ("/api/files?path=expense-attachments/x.jpg"), so strip the "/api" and
// prepend the origin — the same rule getFileUrl uses.
const apiBase = process.env.REACT_APP_API_BASE_URL || "https://uniformapi.solapurmall.com/api";
const serverRoot = apiBase.endsWith("/api") ? apiBase.slice(0, -4) : apiBase.replace(/\/api\/.*$/, "");
export const fileHref = url => (!url ? null : /^https?:\/\//i.test(url) ? url : `${serverRoot}${url}`);

const KINDS = [
  { kind: "BILL", title: "Bills", one: "bill", hint: "The supplier's bill / invoice for what was bought" },
  { kind: "SLIP", title: "Payment Slips", one: "slip", hint: "Proof of payment — UPI / bank screenshot, cash slip, cheque" },
];
const ACCEPT = "image/*,application/pdf,.pdf";
const MAX_BYTES = 10 * 1024 * 1024;   // the server's per-request limit
const fmtDate = s => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");

/**
 * What an entry must carry, by the ledger line's source — the same rule the
 * server applies: an expense and a Hundekari / transport payment need a bill
 * and a payment slip; a staff advance or payment needs a slip (paying a worker
 * has no supplier bill) — and so does an advance handed to a contractor. A slip
 * is needed for cash too — a photo of the voucher.
 */
export const requiredForLine = (source) => {
  switch (source) {
    case "EXPENSE":
    case "HUNDEKARI_PAYMENT":
    case "TRANSPORT_PAYMENT":
      return ["BILL", "SLIP"];
    case "STAFF_ADVANCE":
    case "STAFF_PAYMENT":
    case "CONTRACTOR_ADVANCE":
      return ["SLIP"];
    default:
      return [];
  }
};
export const EXPENSE_REQUIRED = ["BILL", "SLIP"];

const kindLabel = k => (k === "BILL" ? "bill" : "payment slip");
const countKind = (list, k) => (list || []).filter(a => (k === "BILL" ? a.kind === "BILL" : a.kind !== "BILL")).length;

/** Required kinds with nothing on the entry. */
export const missingKinds = (required, list) => (required || []).filter(k => countKind(list, k) === 0);

/**
 * Bills and payment Slips of one expense entry, beside its ledger cell.
 *
 * Shows the counts ("2 bills · 1 slip"); clicking opens them to view, and —
 * with {@code canEdit} — to add photos or PDFs, remove one, or link a bill
 * already uploaded on another entry (one bill paid over several payments).
 * Renders nothing when there is nothing to show and nothing may be added.
 *
 * @param source       EXPENSE | STAFF_LEDGER | HUNDEKARI_LEDGER | TRANSPORT_LEDGER | CONTRACTOR_LEDGER
 * @param entryId      the row id
 * @param attachments  the row's `attachments` from the server (bills first)
 * @param title        what the modal is about, e.g. "EXP/2026/0001 · Diesel"
 */
export default function ExpenseAttachments({ source, entryId, attachments, canEdit = false, title, required }) {
  const [list, setList] = useState(attachments || []);
  const [open, setOpen] = useState(false);
  useEffect(() => { setList(attachments || []); }, [attachments]);

  if (!source || entryId == null) return null;
  const bills = list.filter(a => a.kind === "BILL").length;
  const slips = list.filter(a => a.kind !== "BILL").length;
  // An entry saved before bills and slips were required, or whose upload
  // failed, shows what it lacks — in red, to anyone who can see the row.
  const missing = missingKinds(required, list);
  if (!list.length && !canEdit && !missing.length) return null;

  const label = list.length
    ? [bills && `${bills} bill${bills === 1 ? "" : "s"}`, slips && `${slips} slip${slips === 1 ? "" : "s"}`]
      .filter(Boolean).join(" · ")
    : null;
  const missingText = missing.map(k => `No ${k === "BILL" ? "bill" : "slip"}`).join(" · ");

  return (
    <>
      <button type="button"
        onClick={e => { e.stopPropagation(); setOpen(true); }}
        title={missing.length ? `Required: ${missing.map(kindLabel).join(" and ")} missing`
          : list.length ? "Bills and payment slips" : "Add a bill or payment slip"}
        style={{
          display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6, verticalAlign: "middle",
          border: missing.length ? "1px solid var(--danger)"
            : list.length ? "1px solid var(--primary-dim)" : "1px dashed var(--border)",
          background: missing.length ? "var(--danger-bg)" : list.length ? "var(--primary-light)" : "transparent",
          color: missing.length ? "var(--danger)" : list.length ? "var(--primary)" : "var(--text-2)",
          borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700, cursor: "pointer", lineHeight: 1.6,
        }}>
        <Paperclip size={11} />
        {label || (!missing.length && <Plus size={11} />)}
        {missing.length > 0 && <span>{label ? " · " : ""}{missingText}</span>}
      </button>
      {open && (
        <AttachmentsModal source={source} entryId={entryId} title={title} canEdit={canEdit}
          list={list} onChange={setList} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function AttachmentsModal({ source, entryId, title, canEdit, list, onChange, onClose }) {
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");          // "" | "BILL" | "SLIP" | "link" | "remove"
  const [picking, setPicking] = useState(false);
  const inputs = { BILL: useRef(null), SLIP: useRef(null) };

  // What this entry must carry, from the server (it knows whether a ledger row
  // was paid from an expense advance). Shown on the section headings.
  const [required, setRequired] = useState([]);

  // Fresh list on open — the row may be older than what is on the server.
  useEffect(() => {
    expenseAttachmentApi.list(source, entryId)
      .then(res => onChange(res.data?.data ?? []))
      .catch(e => setErr(e?.response?.data?.message || "Could not load the attachments"));
    expenseAttachmentApi.required(source, entryId)
      .then(res => setRequired(res.data?.data ?? []))
      .catch(() => setRequired([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, entryId]);

  const run = async (what, fn) => {
    setBusy(what); setErr("");
    try {
      const res = await fn();
      onChange(res.data?.data ?? []);
      return true;
    } catch (e) {
      setErr(e?.response?.data?.message || "Something went wrong");
      return false;
    } finally {
      setBusy("");
    }
  };

  // One file per request: the server takes at most 10 MB per request, and a
  // few PDFs together can pass that. The list refreshes after each file, so
  // a failure part-way still shows what did go up.
  const upload = async (kind, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const big = files.filter(f => f.size > MAX_BYTES).map(f => f.name);
    if (big.length) {
      setErr(`${big.join(", ")} ${big.length === 1 ? "is" : "are"} over 10 MB — attach a smaller file.`);
      return;
    }
    setBusy(kind); setErr("");
    try {
      for (const f of files) {
        const res = await expenseAttachmentApi.add(source, entryId, kind, [f]);
        onChange(res.data?.data ?? []);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || "Upload failed");
    } finally {
      setBusy("");
    }
  };
  const remove = a => {
    const shared = a.sharedCount > 1 ? `\n\nThe same file stays on the other ${a.sharedCount - 1} entr${a.sharedCount === 2 ? "y" : "ies"}.` : "";
    if (!window.confirm(`Remove ${a.name || `this ${a.kind === "BILL" ? "bill" : "slip"}`} from this entry?${shared}`)) return;
    run("remove", () => expenseAttachmentApi.remove(a.id));
  };
  const link = async bill => {
    if (await run("link", () => expenseAttachmentApi.link(source, entryId, bill.id))) setPicking(false);
  };

  return (
    // Above the page's own modals — it also opens from inside the advance view.
    <Modal open onClose={onClose} width={760} zIndex={1100} title="Bills & Payment Slips" subtitle={title}
      footer={<Btn variant="secondary" onClick={onClose}>Close</Btn>}>
      <Alert message={err} onClose={() => setErr("")} />
      {KINDS.map(k => {
        const items = list.filter(a => (k.kind === "BILL" ? a.kind === "BILL" : a.kind !== "BILL"));
        return (
          <div key={k.kind} style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-1)" }}>
                  {k.title} <span style={{ color: "var(--text-2)", fontWeight: 600 }}>({items.length})</span>
                  {required.includes(k.kind) && (
                    <span style={{
                      marginLeft: 8, fontSize: 10.5, letterSpacing: 0, textTransform: "none",
                      color: items.length ? "var(--success)" : "var(--danger)",
                    }}>{items.length ? "✓ required" : "required — add at least one"}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-2)" }}>{k.hint}</div>
              </div>
              {canEdit && (
                <>
                  <input ref={inputs[k.kind]} type="file" multiple accept={ACCEPT} style={{ display: "none" }}
                    onChange={e => { upload(k.kind, e.target.files); e.target.value = ""; }} />
                  {k.kind === "BILL" && (
                    <Btn size="sm" variant="outline" icon={<Link2 size={13} />} onClick={() => setPicking(p => !p)}>
                      Pick earlier bill
                    </Btn>
                  )}
                  <Btn size="sm" icon={<Plus size={13} />} loading={busy === k.kind}
                    onClick={() => inputs[k.kind].current?.click()}>
                    Add {k.one}s
                  </Btn>
                </>
              )}
            </div>
            {k.kind === "BILL" && picking && canEdit && (
              <RecentBillPicker onPick={link} busy={busy === "link"} exclude={list} onClose={() => setPicking(false)} />
            )}
            {items.length === 0 ? (
              <div style={{
                padding: "14px", textAlign: "center", fontSize: 13, color: "var(--text-2)",
                border: "1px dashed var(--border)", borderRadius: 8,
              }}>
                No {k.one}s{canEdit ? " — add photos or PDFs" : ""}.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {items.map(a => (
                  <FileTile key={a.id} a={a} onRemove={canEdit ? () => remove(a) : null} disabled={!!busy} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </Modal>
  );
}

/** One attachment: a photo thumbnail or a PDF card; opens in a new tab. */
function FileTile({ a, onRemove, disabled }) {
  const href = fileHref(a.url);
  return (
    <div style={{ position: "relative", width: 132 }}>
      <a href={href} target="_blank" rel="noreferrer" title={a.name || "Open"}
        style={{
          display: "block", width: 132, height: 100, borderRadius: 8, overflow: "hidden",
          border: "1px solid var(--border)", background: "var(--bg-input)", textDecoration: "none",
        }}>
        {a.pdf ? (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
            <FileText size={30} style={{ color: "var(--danger)" }} />
            <span style={{ fontSize: 11, fontWeight: 800, color: "var(--danger)" }}>PDF</span>
          </div>
        ) : (
          <img src={href} alt={a.name || a.kind} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
      </a>
      <div title={a.name} style={{
        fontSize: 11.5, color: "var(--text-1)", marginTop: 3,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{a.name || (a.pdf ? "PDF" : "Photo")}</div>
      <div style={{ fontSize: 10.5, color: "var(--text-2)" }}>
        {fmtDate(a.uploadedAt)}
        {a.sharedCount > 1 && (
          <span title="The same bill is linked to other payments"
            style={{ marginLeft: 4, color: "var(--purple)", fontWeight: 700 }}>· on {a.sharedCount} entries</span>
        )}
      </div>
      {onRemove && (
        <button type="button" title="Remove" disabled={disabled} onClick={onRemove}
          style={{
            position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: 11,
            border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
          <X size={13} />
        </button>
      )}
    </div>
  );
}

/** Earlier bills in the company, to link the same bill to this payment. */
function RecentBillPicker({ onPick, busy, exclude, onClose }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const onEntry = new Set((exclude || []).map(a => a.url));

  useEffect(() => {
    let alive = true;
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await expenseAttachmentApi.recentBills(q.trim());
        if (alive) setRows(res.data?.data ?? []);
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(h); };
  }, [q]);

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 10, marginBottom: 10, background: "var(--bg-input)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Search size={14} style={{ color: "var(--text-2)" }} />
        <input value={q} onChange={e => setQ(e.target.value)} autoFocus
          placeholder="Search earlier bills by party, date, amount or file name…"
          style={{
            flex: 1, padding: "6px 9px", borderRadius: 6, fontSize: 13, outline: "none",
            border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-0)",
          }} />
        <button type="button" onClick={onClose} title="Close"
          style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-2)", display: "flex" }}>
          <X size={16} />
        </button>
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto" }}>
        {loading && <div style={{ fontSize: 12.5, color: "var(--text-2)", padding: 6 }}>Loading…</div>}
        {!loading && rows.length === 0 && (
          <div style={{ fontSize: 12.5, color: "var(--text-2)", padding: 6 }}>No earlier bills found.</div>
        )}
        {!loading && rows.map(b => {
          const already = onEntry.has(b.url);
          return (
            <div key={b.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "6px 4px",
              borderBottom: "1px solid var(--border)", opacity: already ? 0.5 : 1,
            }}>
              <a href={fileHref(b.url)} target="_blank" rel="noreferrer"
                style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)",
                  display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-card)" }}>
                {b.pdf ? <FileText size={22} style={{ color: "var(--danger)" }} />
                  : <img src={fileHref(b.url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
              </a>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-0)" }}>{b.usedOn || b.name || "Bill"}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-2)" }}>
                  {b.name}{b.usedCount > 1 ? ` · on ${b.usedCount} entries` : ""}
                </div>
              </div>
              <Btn size="sm" variant="outline" disabled={already || busy} onClick={() => onPick(b)}>
                {already ? "On this entry" : "Link"}
              </Btn>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── picking bills and slips on a form, before the entry exists ─────────── */

export const EMPTY_STAGED = { BILL: [], SLIP: [], links: [] };

/** Required kinds with nothing on the entry and nothing picked for it. */
export function stagedMissing(required, existing, staged) {
  return (required || []).filter(k => countKind(existing, k) === 0
    && (staged?.[k] || []).length === 0
    && !(k === "BILL" && (staged?.links || []).length > 0));
}

/**
 * Sends what was picked on a form to the entry just saved: one file per
 * request (the server takes 10 MB per request), then the earlier bills
 * linked. Returns how many did not go through — the entry is saved either
 * way, and its row then shows what is still missing.
 */
export async function commitStaged(source, entryId, staged) {
  let failed = 0;
  for (const kind of ["BILL", "SLIP"]) {
    for (const f of staged?.[kind] || []) {
      try { await expenseAttachmentApi.add(source, entryId, kind, [f]); } catch { failed += 1; }
    }
  }
  for (const b of staged?.links || []) {
    try { await expenseAttachmentApi.link(source, entryId, b.id); } catch { failed += 1; }
  }
  return failed;
}

/**
 * Bills and payment slips on a record / edit form. Files are only picked here;
 * {@link commitStaged} sends them once the entry is saved. `existing` is what
 * an edited entry already carries, counted toward what is required.
 */
export function AttachmentStager({ required = [], existing = [], staged, onChange, error }) {
  const [picking, setPicking] = useState(false);
  const [err, setErr] = useState("");
  const inputs = { BILL: useRef(null), SLIP: useRef(null) };
  const s = staged || EMPTY_STAGED;
  const missing = stagedMissing(required, existing, s);

  const addFiles = (kind, fileList) => {
    const files = Array.from(fileList || []);
    const big = files.filter(f => f.size > MAX_BYTES).map(f => f.name);
    setErr(big.length ? `${big.join(", ")} ${big.length === 1 ? "is" : "are"} over 10 MB and left out.` : "");
    const ok = files.filter(f => f.size <= MAX_BYTES);
    if (ok.length) onChange({ ...s, [kind]: [...s[kind], ...ok] });
  };
  const removeFile = (kind, i) => onChange({ ...s, [kind]: s[kind].filter((_, j) => j !== i) });
  const link = b => {
    if (s.links.some(x => x.url === b.url)) return;
    onChange({ ...s, links: [...s.links, b] });
    setPicking(false);
  };

  const chip = (label, onRemove, key, isLink) => (
    <span key={key} style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 6px 3px 9px", borderRadius: 12,
      border: "1px solid var(--border)", background: "var(--bg-app)", fontSize: 12, color: "var(--text-1)",
      maxWidth: 220,
    }}>
      {isLink ? <Link2 size={11} /> : <FileText size={11} />}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <button type="button" onClick={onRemove} title="Remove"
        style={{ border: "none", background: "none", cursor: "pointer", color: "var(--danger)", display: "flex", padding: 0 }}>
        <X size={12} />
      </button>
    </span>
  );

  return (
    <div style={{
      border: `1px solid ${error && missing.length ? "var(--danger)" : "var(--border)"}`,
      borderRadius: 8, padding: 12, background: "var(--bg-card)",
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-2)", marginBottom: 8 }}>
        Bills &amp; payment slips{required.length ? " *" : " (optional)"}
      </div>
      {KINDS.map(k => {
        const need = required.includes(k.kind);
        const have = countKind(existing, k.kind) + s[k.kind].length + (k.kind === "BILL" ? s.links.length : 0);
        return (
          <div key={k.kind} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "var(--text-0)" }}>
                {k.title}
                {need && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: have ? "var(--success)" : "var(--danger)" }}>
                    {have ? "✓" : "required"}
                  </span>
                )}
                {countKind(existing, k.kind) > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 400, color: "var(--text-2)" }}>
                    {countKind(existing, k.kind)} already attached
                  </span>
                )}
              </div>
              <input ref={inputs[k.kind]} type="file" multiple accept={ACCEPT} style={{ display: "none" }}
                onChange={e => { addFiles(k.kind, e.target.files); e.target.value = ""; }} />
              {k.kind === "BILL" && (
                <Btn size="sm" variant="outline" icon={<Link2 size={13} />} onClick={() => setPicking(p => !p)}>
                  Pick earlier bill
                </Btn>
              )}
              <Btn size="sm" variant="secondary" icon={<Plus size={13} />} onClick={() => inputs[k.kind].current?.click()}>
                Add {k.one}s
              </Btn>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--text-2)" }}>{k.hint} — photo or PDF</div>
            {k.kind === "BILL" && picking && (
              <div style={{ marginTop: 6 }}>
                <RecentBillPicker onPick={link} busy={false} exclude={[...(existing || []), ...s.links]}
                  onClose={() => setPicking(false)} />
              </div>
            )}
            {(s[k.kind].length > 0 || (k.kind === "BILL" && s.links.length > 0)) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {s[k.kind].map((f, i) => chip(f.name, () => removeFile(k.kind, i), `${k.kind}${i}`))}
                {k.kind === "BILL" && s.links.map((b, i) => chip(b.usedOn || b.name || "Earlier bill",
                  () => onChange({ ...s, links: s.links.filter((_, j) => j !== i) }), `l${i}`, true))}
              </div>
            )}
          </div>
        );
      })}
      {err && <div style={{ fontSize: 12, color: "var(--warn)" }}>{err}</div>}
      {error && missing.length > 0 && (
        <div style={{ fontSize: 12.5, color: "var(--danger)", fontWeight: 600 }}>
          Add at least one {missing.map(kindLabel).join(" and one ")}.
        </div>
      )}
    </div>
  );
}
